import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import AlternativeFamilyTree from "./FamilyTreeAlternative/AlternativeFamilyTree";
import ReactFamilyTreeView from "./FamilyTreeAlternative/ReactFamilyTreeView";
import SearchBar from "./FamilyTreeAlternative/SearchBar";
import apiService from "../api/apiService";
import { transformApiResponse } from "../utils/apiDataTransformer";
import { useAuth } from "../hooks/useAuth";
import "../scss/familyTree/alternativeFamilyTree.scss";
import "../scss/familyTree/searchBar.scss";

/**
 * FamilyTreePageV2 Component
 * Wrapper component that loads family tree data and allows switching between implementations
 */
const FamilyTreePageV2 = ({ onFamilyTreeLoad }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [familyTree, setFamilyTree] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [focusMemberId, setFocusMemberId] = useState(null);
  const [highlightedMemberId, setHighlightedMemberId] = useState(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [hasSeenHelp, setHasSeenHelp] = useState(false);

  /**
   * Extract family name from slug
   * Example: "sangle-1ftxz32e" -> "Sangle's Family"
   */
  const extractFamilyNameFromSlug = (slug) => {
    if (!slug) return "";
    const parts = slug.split("-");
    const familyName = parts[0];
    // Capitalize first letter
    const capitalized =
      familyName.charAt(0).toUpperCase() + familyName.slice(1);
    return `${capitalized}'s Family`;
  };

  // Set family name from slug immediately
  useEffect(() => {
    if (slug && onFamilyTreeLoad) {
      const fallbackName = extractFamilyNameFromSlug(slug);
      onFamilyTreeLoad(fallbackName);
    }
  }, [slug, onFamilyTreeLoad]);

  /**
   * Load family tree data from API
   */
  const loadFamilyTree = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getFamilyTree(slug);

      // Transform API response to app format
      const transformed = transformApiResponse(response);

      if (transformed.members && transformed.members.length > 0) {
        // Further transform to ensure parent relationships
        const transformedMembers = transformMembersData(transformed.members);

        setFamilyTree(transformed.familyTree);
        setMembers(transformedMembers);

        // Notify parent component about family tree name
        // Use API name if available, otherwise use extracted name from slug
        if (onFamilyTreeLoad) {
          const displayName =
            transformed.familyTree?.familyName ||
            extractFamilyNameFromSlug(slug);
          onFamilyTreeLoad(displayName);
        }

        // Show help modal for new family trees (1 member only) on first load
        const helpKey = `help_seen_${slug}`;
        const hasSeenHelpBefore = localStorage.getItem(helpKey);
        if (!hasSeenHelpBefore && transformedMembers.length === 1) {
          setShowHelpModal(true);
          setHasSeenHelp(true);
        }

        // Set focus to root member if provided, otherwise first member
        if (transformed.familyTree.rootMemberId) {
          setFocusMemberId(transformed.familyTree.rootMemberId);
        } else if (transformedMembers.length > 0) {
          setFocusMemberId(transformedMembers[0]?.id);
        }
      } else {
        setError("No family members found");
      }
    } catch (err) {
      setError(err.message || "Failed to load family tree");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle member selection from search bar
   */
  const handleSelectMember = (member) => {
    if (member && member.id) {
      setHighlightedMemberId(member.id);
      // Clear highlight after animation completes
      setTimeout(() => {
        setHighlightedMemberId(null);
      }, 2000);
    }
  };

  // Load family tree data on mount
  useEffect(() => {
    if (slug) {
      loadFamilyTree();
    }
  }, [slug]);

  /**
   * Convert children relationships to parents relationships
   */
  const transformMembersData = (members) => {
    // Create a map of id -> parents array
    const parentMap = {};

    members.forEach((member) => {
      if (!parentMap[member.id]) {
        parentMap[member.id] = [];
      }
    });

    // Build parent relationships from children relationships
    members.forEach((member) => {
      if (member.children && Array.isArray(member.children)) {
        member.children.forEach((childId) => {
          if (!parentMap[childId]) {
            parentMap[childId] = [];
          }
          parentMap[childId].push(member.id);
        });
      }
    });

    // Create transformed members with parents property
    const transformedMembers = members.map((member) => ({
      ...member,
      parents: parentMap[member.id] || [],
    }));

    return transformedMembers;
  };

  /**
   * Handle member updates (from editing or adding)
   */
  const handleMemberUpdate = async (updatedMember) => {
    // If it's a delete operation, reload the tree
    if (updatedMember?.deleted) {
      try {
        // Reload the entire family tree after deletion
        await loadFamilyTree();
      } catch (error) {
        console.error("Failed to reload tree after deletion:", error);
      }
      return;
    }

    try {
      // Show loading spinner
      Swal.fire({
        title: "Updating member...",
        html: '<div class="custom-spinner"></div>',
        allowOutsideClick: false,
        showConfirmButton: false,
        // didOpen: () => {
        //   Swal.showLoading();
        // },
      });

      console.log("FamilyTreePageV2 - Updating member:", updatedMember);

      // Call API to update the member
      await apiService.updateMember(slug, updatedMember.id, updatedMember);

      // Reload the entire family tree to get updated relationships
      await loadFamilyTree();

      console.log("FamilyTreePageV2 - Member updated and tree reloaded");

      // Show success message
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Member updated successfully",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to update member:", error);

      // Show error message
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error?.message || "Failed to update member. Please try again.",
        confirmButtonColor: "#d33",
      });

      throw error;
    }
  };

  /**
   * Handle adding a new member
   */
  const handleAddMember = async (memberData) => {
    try {
      // Show loading spinner
      Swal.fire({
        title: "Adding member...",
        html: '<div class="custom-spinner"></div>',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Call API to create the member
      const response = await apiService.createMember(slug, memberData);

      if (response?.data?.member || response?.data?.success) {
        // Reload the family tree to get the new member with all relationships
        await loadFamilyTree();

        // Show success message
        Swal.fire({
          icon: "success",
          title: "Member Added!",
          text: "New family member has been added successfully",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      // Show error message
      Swal.fire({
        icon: "error",
        title: "Failed to Add Member",
        text:
          error?.message ||
          "Could not add the family member. Please try again.",
        confirmButtonColor: "#d33",
      });

      throw error;
    }
  };

  return (
    <div className="family-tree-page">
      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <div className="spinner">
            <div className="spinner-ring"></div>
          </div>
          <p>Loading family tree...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="error-container">
          <div className="error-content">
            <div className="error-icon-animated">
              <div className="tree-wilting">
                <span className="wilted-tree">🌳</span>
                <div className="falling-leaves">
                  <span className="falling-leaf">🍂</span>
                  <span className="falling-leaf">🍂</span>
                  <span className="falling-leaf">🍁</span>
                </div>
              </div>
            </div>
            <h2 className="error-title">This Family Tree Couldn't Be Found</h2>
            <p className="error-message">
              {error === "Family tree not found"
                ? "It seems this family tree doesn't exist or has been removed from our forest. Perhaps it was never planted, or the seeds didn't take root."
                : error}
            </p>
            <div className="error-suggestions">
              <p className="suggestion-title">Here's what you can do:</p>
              <ul>
                <li>🏠 Start a new family tree from the homepage</li>
                <li>🔍 Check if the link was copied correctly</li>
                <li>📧 Contact the person who shared this link</li>
              </ul>
            </div>
            <div className="error-actions">
              <button className="btn-primary" onClick={() => navigate("/")}>
                🏠 Go to Homepage
              </button>
              <button className="btn-secondary" onClick={() => navigate(-1)}>
                ← Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Family Tree Visualization */}
      {!loading && !error && members.length > 0 && (
        <div className="tree-content">
          <ReactFamilyTreeView
            members={members}
            onMemberUpdate={handleMemberUpdate}
            focusMemberId={focusMemberId}
            title={familyTree?.familyName || "Family Tree"}
            showToolbar={true}
            slug={slug}
            onShowHelp={() => setShowHelpModal(true)}
            parentLoading={loading}
            familyTree={familyTree}
          />
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && members.length === 0 && (
        <div className="empty-container">
          <p>No family members to display</p>
        </div>
      )}

      {/* Help Modal */}
      {showHelpModal && (
        <div
          className="help-modal-overlay"
          onClick={() => {
            setShowHelpModal(false);
            if (hasSeenHelp && slug) {
              localStorage.setItem(`help_seen_${slug}`, "true");
            }
          }}
        >
          <div
            className="help-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="help-modal-header">
              <div className="header-icon-wrapper">
                <span className="header-icon">📖</span>
                <div className="icon-sparkles">
                  <span className="sparkle">✨</span>
                  <span className="sparkle">✨</span>
                  <span className="sparkle">✨</span>
                </div>
              </div>
              <h2>How to Use Your Family Tree</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowHelpModal(false);
                  if (hasSeenHelp && slug) {
                    localStorage.setItem(`help_seen_${slug}`, "true");
                  }
                }}
              >
                ✕
              </button>
            </div>
            <div className="help-modal-body">
              <div className="help-section sharing-section">
                <div className="section-header">
                  <span className="section-icon">🔗</span>
                  <h3>Sharing Your Family Tree</h3>
                </div>
                <p>Share this link with your family members:</p>
                <div className="help-code-block">
                  <code>{`${window.location.origin}/family/${slug}`}</code>
                  <button
                    className="copy-btn-mini"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${window.location.origin}/family/${slug}`
                      );
                    }}
                    title="Copy link"
                  >
                    📋
                  </button>
                </div>
                <p className="help-highlight">
                  Anyone with this link can view and contribute to the family
                  tree!
                </p>
                <div className="help-warning-box">
                  <div className="warning-icon">⚠️</div>
                  <div className="warning-content">
                    <strong className="warning-title">
                      Important Security Notice:
                    </strong>
                    <p>
                      <strong className="warning-emphasis">Anyone</strong> with
                      this link can{" "}
                      <strong className="warning-emphasis">
                        add, edit, delete
                      </strong>
                      , and{" "}
                      <strong className="warning-emphasis">
                        access all information
                      </strong>{" "}
                      in your family tree. They can also{" "}
                      <strong className="warning-emphasis">
                        share this link
                      </strong>{" "}
                      with others.
                    </p>
                    <p className="warning-advice">
                      Please share this link carefully and only with trusted
                      family members.
                    </p>
                  </div>
                </div>
              </div>

              <div className="help-section adding-section">
                <div className="section-header">
                  <span className="section-icon">➕</span>
                  <h3>Adding Family Members</h3>
                </div>
                <div className="step-list">
                  <div className="step-item">
                    <span className="step-number">1</span>
                    <span>Click on any existing family member's card</span>
                  </div>
                  <div className="step-item">
                    <span className="step-number">2</span>
                    <span>
                      Use the <strong>"Add Member"</strong> button
                    </span>
                  </div>
                  <div className="step-item">
                    <span className="step-number">3</span>
                    <span>
                      Select the relationship (Parent, Child, Spouse, or
                      Sibling)
                    </span>
                  </div>
                  <div className="step-item">
                    <span className="step-number">4</span>
                    <span>
                      Fill in the member's details (name and gender required)
                    </span>
                  </div>
                  <div className="step-item">
                    <span className="step-number">5</span>
                    <span>Click the green checkmark to save</span>
                  </div>
                </div>
              </div>

              <div className="help-section editing-section">
                <div className="section-header">
                  <span className="section-icon">✏️</span>
                  <h3>Editing Members</h3>
                </div>
                <div className="step-list">
                  <div className="step-item">
                    <span className="step-number">1</span>
                    <span>Click on a family member's card</span>
                  </div>
                  <div className="step-item">
                    <span className="step-number">2</span>
                    <span>
                      Click the <strong>"Edit"</strong> button (pencil icon)
                    </span>
                  </div>
                  <div className="step-item">
                    <span className="step-number">3</span>
                    <span>Update any information</span>
                  </div>
                  <div className="step-item">
                    <span className="step-number">4</span>
                    <span>
                      Click <strong>"Save"</strong> to apply changes
                    </span>
                  </div>
                </div>
              </div>

              <div className="help-section images-section">
                <div className="section-header">
                  <span className="section-icon">🖼️</span>
                  <h3>Managing Images</h3>
                </div>
                <div className="feature-grid">
                  <div className="feature-card">
                    <span className="feature-icon">📸</span>
                    <p>
                      Click <strong>"Manage Images"</strong> in the edit modal
                    </p>
                  </div>
                  <div className="feature-card">
                    <span className="feature-icon">🔗</span>
                    <p>Add Google Drive image links</p>
                  </div>
                  <div className="feature-card">
                    <span className="feature-icon">🔄</span>
                    <p>Toggle between Profile Photo and Gallery</p>
                  </div>
                  <div className="feature-card">
                    <span className="feature-icon">🏷️</span>
                    <p>Click badges to change image type</p>
                  </div>
                </div>
              </div>

              <div className="help-section family-section">
                <div className="section-header">
                  <span className="section-icon">👨‍👩‍👧‍👦</span>
                  <h3>Building Your Family Tree</h3>
                </div>
                <div className="encouragement-box">
                  <p className="encouragement-title">
                    <strong>Encourage your family members to:</strong>
                  </p>
                  <div className="encouragement-items">
                    <div className="encouragement-item">
                      <span className="check-icon">✓</span>
                      <span>Visit the shared link</span>
                    </div>
                    <div className="encouragement-item">
                      <span className="check-icon">✓</span>
                      <span>Add their own information</span>
                    </div>
                    <div className="encouragement-item">
                      <span className="check-icon">✓</span>
                      <span>Add photos and details about relatives</span>
                    </div>
                    <div className="encouragement-item">
                      <span className="check-icon">✓</span>
                      <span>Connect generations</span>
                    </div>
                    <div className="encouragement-item">
                      <span className="check-icon">✓</span>
                      <span>Keep information updated</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="help-note">
                <span className="note-icon">💡</span>
                <div>
                  <strong>Tip:</strong> Everyone with the link can edit! Work
                  together to build a complete family history.
                </div>
              </div>
            </div>
            <div className="help-modal-footer">
              <button
                className="proceed-btn"
                onClick={() => {
                  setShowHelpModal(false);
                  if (hasSeenHelp && slug) {
                    localStorage.setItem(`help_seen_${slug}`, "true");
                  }
                }}
              >
                Got it, Let's Start!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyTreePageV2;
